/**
 * `PaymentTransactionsTable` — the REUSABLE payments-ledger table (v2 —
 * Transaction Ledger, DNKADM-0149 U2).
 *
 * Presentational by default: it takes already-parsed {@link PaymentTransactionRow}s and
 * a `memberScoped` flag and renders the newest-first money-moved rows (the one write
 * affordance is opt-in — see Order reversal below). It owns NO
 * loading/paging/filter state — the screen (`TransactionLedgerScreen`, and the
 * member-detail Transactions tab in DNKADM-0150) supplies the rows and wraps it with
 * the loading skeleton / empty / error+retry states. `memberScoped` hides the Contact
 * column (every row is the same member on the member-detail tab).
 *
 * Row rendering (spec §5): `createdAt`, contact (`contactName ?? contactId`), `kind`,
 * a `status` chip (with the DNKAPI-0676 decline-reason line, else `failureCode`, shown on a
 * `failed` row), `gross`/`tax`/`total`
 * via **`formatMoney(_, currency)`** (a null currency degrades to a plain 2-dp number
 * — never a throw), `requestedLocationRef`, and a reference cell: a `reservationUuid`
 * → the `/reservations?reservation=<uuid>` deep link (DNKADM-0147); else the plain
 * `reservationId`; a `membershipPlanId` → `/membership-credits/plans`; a refund row
 * (`kind:'refund'`) → its `parentProviderRef`. Enrichment is best-effort — a null name
 * falls back to the id, never an error.
 *
 * **Order reversal (DNKADM-0251) — OPT-IN.** Passing `onReversed` adds an Actions
 * column offering the reversal verb on a reversible row — ONLY the verb the row's
 * `orderPaymentMode` permits since DNKADM-0305 (`comp` → Void; `card`/`external` →
 * Refund; unresolved mode → both, as before) — wired to the order-scoped
 * `refundMemberOrder`/`voidMemberOrder`; omitting it renders the table exactly as
 * before (no Actions header, no Actions cell), so a read-only caller stays read-only.
 * A row is reversible ONLY with a non-null `orderId` (the sole linkage — `providerRef`
 * is never parsed for this) and a `contactId` (the endpoints are member-scoped);
 * anything else — no order, already reversed, failed — shows NO action rather than a
 * disabled one (dink-api's "hide the action" ruling, 2026-08-04). Because a
 * money-moving verb sits in a dense row, every reversal goes through a confirm dialog
 * naming the verb, the amount and the contact; dismissing it issues no request. The
 * row is never patched locally — the reversal also creates a NEW `kind:'refund'` row,
 * so only the caller's re-read (`onReversed`) is authoritative.
 */
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useLocationName } from '@/location/useLocationName';
import { Icon, MONO, resolveTokens } from '@/theme';
import { buildTransactionLedgerMaps } from '@/theme/maps/transactionLedger';
import { declineReasonLine } from '@/utils/declineReason';
import { REFUND_IN_PROGRESS_MESSAGE, refundProcessingText } from '@/utils/refundInProgress';
import { formatMoney } from '@/utils/v2Format';
import { scrubBrand } from '@/utils/whitelabel';
import { UNNAMED_MEMBER } from '@/utils/personLabel';
import { MemberNameLink } from '@/components/MemberNameLink';
import { serverMessage } from '@/api/serverMessage';
import { refundMemberOrder, voidMemberOrder } from '@/api/membershipOrders';
import type { PaymentTransactionRow } from '@/api/paymentsTransactions';
import { ReservationRefCell } from './transactionCells';
import { formatTransactionTime, humanizeToken } from './transactionFormat';

/**
 * Format a money amount in `currency`. A null/blank currency (best-effort wire)
 * degrades to a plain 2-decimal number rather than throwing on `Intl` with no code.
 */
function money(amount: number, currency: string | null): string {
  if (currency === null) return amount.toFixed(2);
  try {
    return formatMoney(amount, currency);
  } catch {
    return amount.toFixed(2);
  }
}

function StatusChip({ status }: { status: string }) {
  const maps = buildTransactionLedgerMaps(useTheme().palette.mode);
  const tint =
    (maps.paymentStatus as Record<string, { bg: string; fg: string }>)[status] ??
    maps.paymentStatusFallback;
  return (
    <Chip
      size="small"
      label={humanizeToken(status)}
      sx={{ height: 22, fontSize: 11.5, fontWeight: 600, bgcolor: tint.bg, color: tint.fg }}
    />
  );
}

/** A charge with a refund in flight (DNKAPI-0658). Refund rows are never flagged. */
function refundProcessing(row: PaymentTransactionRow): boolean {
  return row.kind !== 'refund' && row.refundInProgress === true;
}

/** The two order-scoped reversal verbs (`api/membershipOrders.ts`). */
type ReversalAction = 'refund' | 'void';

/** Per-verb copy: the row button, the confirm dialog, and the failure fallback. */
const REVERSAL_COPY: Record<ReversalAction, {
  button: string;
  icon: string;
  title: string;
  confirm: string;
  failure: string;
}> = {
  refund: {
    button: 'Refund',
    icon: 'undo',
    title: 'Refund this order?',
    confirm: 'Refund order',
    failure: 'The order could not be refunded.',
  },
  void: {
    button: 'Void',
    icon: 'block',
    title: 'Void this order?',
    confirm: 'Void order',
    failure: 'The order could not be voided.',
  },
};

/** The row a confirm dialog is open for, with the identifiers the call needs. */
interface ReversalTarget {
  row: PaymentTransactionRow;
  action: ReversalAction;
  orderId: number;
  contactId: string;
}

/**
 * The confirm dialog's consequence line (DNKADM-0303 U5): each verb states
 * what actually happens, in plain words — never a generic "are you sure".
 * Refund and Void are both terminal but NOT interchangeable
 * (`OrderReversalService`): Refund moves money back; Void stamps the order
 * reversed WITHOUT moving money. Since DNKADM-0305 the row can KNOW the
 * order's tender (`orderPaymentMode`, DNKAPI-0467/0468): the external-refund
 * branch keys on it first (the pre-0305 `provider === 'manual'` heuristic
 * stays as the null-mode fallback), and a known `comp` void states plainly
 * that nothing was taken because the order is a comp. Null-mode copy is the
 * 0304 wording unchanged.
 */
function reversalConsequence(target: ReversalTarget): string {
  const amount = money(target.row.total, target.row.currency);
  const contact = contactLabel(target.row);
  const mode = target.row.orderPaymentMode;
  const tail = `This reverses order ${target.orderId} and can’t be undone.`;
  if (target.action === 'void') {
    if (mode === 'comp') {
      return (
        `Void this order for ${contact} — it is a comp: no money was taken, so none is returned. ` +
        `Unused credits from it are revoked, and a subscription it created is cancelled. ${tail}`
      );
    }
    return (
      `Void this order for ${contact} — no money will be returned. ` +
      `Void is only correct when no money was actually taken (a comped or externally settled order); ` +
      `if the card was charged, Refund is what returns the ${amount}. ${tail}`
    );
  }
  if (mode === 'external' || (mode === null && target.row.provider === 'manual')) {
    return (
      `Refund ${amount} for ${contact}. This order was recorded as paid externally, so no money moves here — ` +
      `the order is marked refunded, unused credits from it expire, and a subscription it created is cancelled. ` +
      `Return the payment the way it was originally taken. ${tail}`
    );
  }
  return (
    `Refund ${amount} to ${contact}’s card on file — the money goes back through the payment provider. ` +
    `Unused credits from this order expire, and a subscription it created is cancelled. ${tail}`
  );
}

/**
 * The contact as displayed — backend text, so `scrubBrand`'d at this render boundary
 * (DNKADM-0044) before it reaches a cell, an accessible name, or confirm copy.
 * Enrichment is best-effort: a null name reads "Unnamed member", never the raw id
 * (DNKADM-0454 V-68/V-69).
 */
function contactLabel(row: PaymentTransactionRow): string {
  return scrubBrand(row.contactName) ?? UNNAMED_MEMBER;
}

/**
 * Can this row be reversed in the ledger, and with what identifiers?
 *
 * Mirrors `BuyMembershipOrderDialog`'s shipped refund-vs-void policy
 * (`:311-316` / `:760-781`): a control exists ONLY while the order
 * is `COMPLETED` with a non-null `orderId` — no other state gets a control at all
 * (which VERB(S) render is {@link offeredActions}' narrowing, DNKADM-0305). The
 * row-level analogue of `COMPLETED` is a `succeeded` transaction that is not itself a
 * reversal (`kind:'refund'` — a refund row's own order is already reversed). `contactId`
 * is required because the endpoints are member-scoped. Returns `null` ⇒ render no
 * action, never a disabled one.
 */
function reversalIdentifiers(
  row: PaymentTransactionRow,
): { orderId: number; contactId: string } | null {
  if (row.orderId === null || row.contactId === null) return null;
  if (row.status !== 'succeeded' || row.kind === 'refund') return null;
  return { orderId: row.orderId, contactId: row.contactId };
}

/**
 * The verbs the row's KNOWN payment mode permits (DNKADM-0305) — the mapping
 * DNKAPI-0467's contract states verbatim and `OrderReversalService
 * .requireReversible` enforces: a `comp` took no money → Void only; `card`
 * and `external` took money (external returns it outside dink) → Refund
 * only. A `null` mode (no order resolved / enrichment failed) degrades to
 * BOTH verbs — the pre-0305 offer; the server stays the authority either
 * way. This narrows WHICH verb renders, never whether an action exists —
 * {@link reversalIdentifiers} alone decides that.
 */
function offeredActions(row: PaymentTransactionRow): readonly ReversalAction[] {
  if (row.orderPaymentMode === 'comp') return ['void'];
  if (row.orderPaymentMode === 'card' || row.orderPaymentMode === 'external') return ['refund'];
  return ['refund', 'void'];
}

export interface PaymentTransactionsTableProps {
  rows: PaymentTransactionRow[];
  /** When true (member-detail tab, DNKADM-0150) the Contact column is hidden. */
  memberScoped?: boolean;
  /** Re-read the page after a reversal. Omit to render the table read-only. */
  onReversed?: () => void;
  /** Venue IANA zone from the screen's location context; null renders in the browser zone. */
  timeZone?: string | null;
}

/**
 * The note under a `failed` row's status chip: the DNKAPI-0676 decline-reason line when the
 * API knows why the card was declined, else the humanized failure code (as before), else none.
 */
function failureNote(row: PaymentTransactionRow): string | null {
  if (row.status !== 'failed') return null;
  const reasonLine = declineReasonLine(row.declineReason);
  if (reasonLine !== null) return reasonLine;
  return row.failureCode !== null ? humanizeToken(row.failureCode) : null;
}

/** The reusable payments-ledger table (presentational unless `onReversed` opts in). */
export function PaymentTransactionsTable({
  rows,
  memberScoped = false,
  onReversed,
  timeZone = null,
}: PaymentTransactionsTableProps) {
  const t = resolveTokens(useTheme());
  const locationName = useLocationName();
  const [target, setTarget] = useState<ReversalTarget | null>(null);
  const [reversing, setReversing] = useState(false);
  const [reversalError, setReversalError] = useState<string | null>(null);
  // Opt-in: without a refresh callback there is no Actions column at all.
  const actionable = onReversed !== undefined;

  const closeConfirm = () => {
    // Dismissing the confirm issues NO request — the mis-click guard.
    setTarget(null);
    setReversalError(null);
  };

  const openConfirm = (next: ReversalTarget) => {
    setReversalError(null);
    setTarget(next);
  };

  async function submitReversal() {
    if (target === null || reversing) return;
    setReversing(true);
    setReversalError(null);
    try {
      if (target.action === 'refund') {
        await refundMemberOrder(target.contactId, target.orderId);
      } else {
        await voidMemberOrder(target.contactId, target.orderId);
      }
      setTarget(null);
      // The server's response is authoritative and the reversal also creates a NEW
      // kind:'refund' row — so re-read rather than patching this row locally.
      onReversed?.();
    } catch (err) {
      // Failure leaves the row (and the dialog) exactly as it was — never a false success.
      setReversalError(serverMessage(err) ?? REVERSAL_COPY[target.action].failure);
    } finally {
      setReversing(false);
    }
  }

  return (
    <>
      <TableContainer>
        <Table aria-label="Payment transactions" size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              {!memberScoped && <TableCell>Contact</TableCell>}
              <TableCell>Kind</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Gross</TableCell>
              <TableCell align="right">Tax</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Reference</TableCell>
              {actionable && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{formatTransactionTime(row.createdAt, timeZone)}</TableCell>
                {!memberScoped && (
                  <TableCell>
                    <MemberNameLink name={scrubBrand(row.contactName)} contactId={row.contactId} />
                  </TableCell>
                )}
                <TableCell>{row.kind !== null ? humanizeToken(row.kind) : '—'}</TableCell>
                <TableCell>
                  <Stack sx={{ gap: 0.5, alignItems: 'flex-start' }}>
                    {row.status !== null ? <StatusChip status={row.status} /> : <span>—</span>}
                    {refundProcessing(row) && (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={refundProcessingText(row.refundInProgressAmount, row.currency)}
                        sx={{ height: 22, fontSize: 11.5, fontWeight: 600 }}
                      />
                    )}
                    {failureNote(row) !== null && (
                      <Typography sx={{ fontSize: 11, color: t.error, maxWidth: 260 }}>
                        {failureNote(row)}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">{money(row.gross, row.currency)}</TableCell>
                <TableCell align="right">{money(row.tax, row.currency)}</TableCell>
                <TableCell align="right">{money(row.total, row.currency)}</TableCell>
                <TableCell>{locationName(row.requestedLocationRef) ?? '—'}</TableCell>
                <TableCell>
                  <Stack sx={{ gap: 0.25, alignItems: 'flex-start' }}>
                    <ReservationRefCell reservationUuid={row.reservationUuid} reservationId={row.reservationId} />
                    {row.membershipPlanId !== null && (
                      <Link component={RouterLink} to="/membership-credits/plans" sx={{ fontSize: 12 }}>
                        Plan
                      </Link>
                    )}
                    {row.kind === 'refund' && row.parentProviderRef !== null && (
                      <Box component="span" sx={{ fontFamily: MONO, fontSize: 11, color: t.textSubtle }}>
                        Refund of {row.parentProviderRef}
                      </Box>
                    )}
                  </Stack>
                </TableCell>
                {actionable && (
                  <ReversalCell
                    row={row}
                    busy={reversing && target?.row.id === row.id}
                    onPick={openConfirm}
                  />
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {target !== null && (
        <ReversalConfirmDialog
          target={target}
          reversing={reversing}
          error={reversalError}
          onCancel={closeConfirm}
          onConfirm={() => void submitReversal()}
        />
      )}
    </>
  );
}

/**
 * The per-row Actions cell. Renders the two verbs only for a reversible row
 * ({@link reversalIdentifiers}); otherwise the cell stays empty — no disabled control.
 * A table carries many identical buttons, so each accessible name identifies the ROW
 * (order + contact), not just the verb.
 */
function ReversalCell({
  row,
  busy,
  onPick,
}: {
  row: PaymentTransactionRow;
  busy: boolean;
  onPick: (target: ReversalTarget) => void;
}) {
  const identifiers = reversalIdentifiers(row);
  if (identifiers === null) return <TableCell align="right" />;
  const contact = contactLabel(row);
  const processing = refundProcessing(row);

  return (
    <TableCell align="right">
      <Stack direction="row" sx={{ gap: 0.5, justifyContent: 'flex-end' }}>
        {/* Only the verb the order's known mode permits (DNKADM-0305) — an
            unresolved mode degrades to both, exactly the pre-0305 offer. */}
        {offeredActions(row).map((action) => (
          <Button
            key={action}
            size="small"
            color="error"
            disabled={busy || (processing && action === 'refund')}
            aria-label={`${REVERSAL_COPY[action].button} order ${identifiers.orderId} for ${contact}`}
            onClick={() => onPick({ row, action, ...identifiers })}
          >
            {REVERSAL_COPY[action].button}
          </Button>
        ))}
      </Stack>
      {processing && offeredActions(row).includes('refund') && (
        <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5 }}>
          {REFUND_IN_PROGRESS_MESSAGE}
        </Typography>
      )}
    </TableCell>
  );
}

/**
 * The mandatory confirm step. It states the verb's CONSEQUENCE — the amount
 * (`total` in the row's currency), the contact, and whether money actually
 * moves ({@link reversalConsequence}, DNKADM-0303 U5) — because a mis-click
 * in a dense ledger row must never be able to move (or falsely un-record)
 * money. Cancel/dismiss issues no request; a failure keeps the dialog open
 * with the server's message.
 */
function ReversalConfirmDialog({
  target,
  reversing,
  error,
  onCancel,
  onConfirm,
}: {
  target: ReversalTarget;
  reversing: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const copy = REVERSAL_COPY[target.action];

  return (
    <Dialog open onClose={reversing ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{copy.title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: 14 }}>{reversalConsequence(target)}</DialogContentText>
        {error !== null && (
          <Alert severity="error" role="alert" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="inherit" disabled={reversing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<Icon name={copy.icon} />}
          onClick={onConfirm}
          disabled={reversing}
        >
          {copy.confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
